/**
 * Client-side cache for rendered report PDFs / Excel files.
 *
 * Each render endpoint returns an ETag = a fingerprint of the data + layout +
 * branding. We store the blob in IndexedDB keyed by a stable cache key, and on
 * the next request send `If-None-Match` with the stored ETag. If the data hasn't
 * changed the server replies 304 (skipping the expensive puppeteer / Excel
 * render) and we serve the stored blob — instant, and zero server render cost.
 *
 * IndexedDB (not localStorage) because the values are binary PDF/xlsx blobs.
 * Capped to MAX_ENTRIES, evicting least-recently-used. Every step degrades
 * gracefully to "just fetch normally" if IndexedDB is unavailable (private mode).
 */
import type { AxiosInstance } from 'axios';

const DB_NAME = 'costed-render-cache';
const STORE = 'renders';
const MAX_ENTRIES = 60;

interface CacheRecord {
  key: string;
  etag: string;
  blob: Blob;
  touched: number;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'key' });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

function idbGet(key: string): Promise<CacheRecord | null> {
  return openDb().then((db) => {
    if (!db) return null;
    return new Promise<CacheRecord | null>((resolve) => {
      try {
        const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(key);
        req.onsuccess = () => resolve((req.result as CacheRecord) ?? null);
        req.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
  });
}

function idbPut(rec: CacheRecord): Promise<void> {
  return openDb().then((db) => {
    if (!db) return;
    return new Promise<void>((resolve) => {
      try {
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).put(rec);
        tx.oncomplete = () => { void evictIfNeeded(); resolve(); };
        tx.onerror = () => resolve();
      } catch {
        resolve();
      }
    });
  });
}

async function evictIfNeeded(): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    const store = db.transaction(STORE, 'readwrite').objectStore(STORE);
    const all = await new Promise<CacheRecord[]>((resolve) => {
      const req = store.getAll();
      req.onsuccess = () => resolve((req.result as CacheRecord[]) ?? []);
      req.onerror = () => resolve([]);
    });
    if (all.length <= MAX_ENTRIES) return;
    all.sort((a, b) => a.touched - b.touched); // oldest first
    for (const rec of all.slice(0, all.length - MAX_ENTRIES)) store.delete(rec.key);
  } catch {
    /* ignore */
  }
}

/** Stable cache-key fragment for a filter object (order-independent). */
export function stableKey(filters?: Record<string, string | number | boolean>): string {
  if (!filters) return '';
  const sorted: Record<string, unknown> = {};
  for (const k of Object.keys(filters).sort()) sorted[k] = filters[k];
  return JSON.stringify(sorted);
}

/**
 * POST that resolves to a Blob, with ETag/304 caching.
 *  - sends `If-None-Match` from the cached ETag (if any)
 *  - on 304: returns the cached blob (server skipped the render)
 *  - on 200: caches the new blob + ETag and returns it
 */
export async function cachedBlobPost(
  client: AxiosInstance,
  url: string,
  body: unknown,
  cacheKey: string,
): Promise<Blob> {
  const cached = await idbGet(cacheKey);
  const headers: Record<string, string> = {};
  if (cached?.etag) headers['If-None-Match'] = cached.etag;

  const res = await client.post(url, body, {
    responseType: 'blob',
    headers,
    validateStatus: (s) => s === 200 || s === 304,
  });

  if (res.status === 304 && cached) {
    void idbPut({ ...cached, touched: Date.now() }); // bump LRU
    return cached.blob;
  }

  const blob = res.data as Blob;
  const etag = res.headers?.['etag'] as string | undefined;
  if (etag && blob && blob.size > 0) {
    void idbPut({ key: cacheKey, etag, blob, touched: Date.now() });
  }
  return blob;
}
